import { Configuration, type FetchAPI } from "@openapi";
import * as APIS from "@openapi/apis";

// The generated backend client, pointed at this app's /api/proxy route.
// The browser holds the session as an httpOnly cookie that the proxy
// (src/app/api/proxy) turns into the backend's Bearer token, so no token
// is handled here and the browser sends the cookie by itself.

/** Dispatched on window when a call fails with 401 while logged in. */
export const SESSION_EXPIRED_EVENT = "auth:unauthorized";

// Backend paths where a 401 means wrong credentials or an already ended
// session, not an expired one, so it must not log the user out.
const CREDENTIAL_PATHS = ["/auth/login", "/auth/register", "/auth/logout"];

const isCredentialCall = (url: string): boolean => {
    const pathname = new URL(url, window.location.origin).pathname;
    return CREDENTIAL_PATHS.some((path) => pathname.endsWith(path));
};

const requestUrl = (input: RequestInfo | URL): string =>
    typeof input === "string"
        ? input
        : input instanceof URL
          ? input.href
          : input.url;

// The response goes back to the generated client unchanged, so a 401 still
// fails the call for the caller; AuthContext handles the event.
const sessionAwareFetch: FetchAPI = async (input, init) => {
    const response = await fetch(input, init);
    if (
        response.status === 401 &&
        typeof window !== "undefined" &&
        !isCredentialCall(requestUrl(input))
    ) {
        window.dispatchEvent(new CustomEvent(SESSION_EXPIRED_EVENT));
    }
    return response;
};

const config = new Configuration({
    basePath: "/api/proxy",
    fetchApi: sessionAwareFetch,
});

const api = {
    users: new APIS.UsersApi(config),
    competitions: new APIS.CompetitionsApi(config),
    events: new APIS.EventsApi(config),
    competitionLeagues: new APIS.CompetitionLeaguesApi(config),
    competitionSeasons: new APIS.CompetitionSeasonsApi(config),
    problems: new APIS.ProblemsApi(config),
    submissions: new APIS.SubmissionsApi(config),
    testCases: new APIS.TestCasesApi(config),
    testCaseOutputs: new APIS.TestCaseOutputsApi(config),
    organizations: new APIS.OrganizationsApi(config),
    roles: new APIS.RolesApi(config),
    codes: new APIS.CodesApi(config),
    licenses: new APIS.LicensesApi(config),
    superadmin: new APIS.SuperadminApi(config),
    auth: new APIS.AuthApi(config),
    uploads: new APIS.UploadsApi(config),
    userReports: new APIS.UserReportsApi(config),
    default: new APIS.DefaultApi(config),
    judge0: new APIS.Judge0Api(config),
};

export default api;
